/**
 * LayerSection.jsx
 * Shared collapsible section header used to group the app's three real
 * hierarchy layers (Season / Competitions / Matches) consistently across
 * Home and Create & Manage, instead of each view inventing its own grouping.
 * Collapse state is remembered per section per user via localStorage, so an
 * athlete who collapses "Season" once doesn't have to redo it every visit.
 * See docs_zenanalytics/25-STRUCTURE-AND-CAREER-RECORD-PLANNING-2026-10-28.md.
 *
 * Deliberately does NOT draw its own card background — the sections it
 * wraps (WhatsNext, SeasonSummary, EquipmentStatModule, etc.) already have
 * their own bordered-card chrome, and a second background behind them would
 * just nest cards inside cards. LayerSection only owns the header row
 * (icon + title + optional right-side actions + collapse chevron) and
 * whether the body renders at all.
 */
import React, { useState, useEffect } from 'react';
import { onEnterOrSpace } from '../utils/a11y';

export default function LayerSection({ id, userId, icon, title, rightSlot, defaultOpen = true, children }) {
  const storageKey = `zenanalytics_layer_${id}_${userId || 'anon'}`;

  const [open, setOpen] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved === null ? defaultOpen : saved === '1';
    } catch {
      return defaultOpen;
    }
  });

  // Re-read when the storage key itself changes (e.g. userId resolves after
  // an initial anonymous render) so the remembered state still applies.
  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved !== null) setOpen(saved === '1');
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  const toggle = () => {
    setOpen(o => {
      const next = !o;
      try { localStorage.setItem(storageKey, next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };

  return (
    <div style={{ marginBottom: 14 }}>
      <div
        onClick={toggle}
        role="button"
        tabIndex={0}
        onKeyDown={onEnterOrSpace(toggle)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          cursor: 'pointer', userSelect: 'none', padding: '4px 2px 8px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 15 }}>{icon}</span>
          <span style={{
            color: '#38bdf8', fontSize: 12.5, fontWeight: 800,
            textTransform: 'uppercase', letterSpacing: '0.07em',
          }}>{title}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }} onClick={e => e.stopPropagation()}>
          {rightSlot}
          <span
            onClick={toggle}
            role="button" tabIndex={-1}
            style={{
              color: '#64748b', fontSize: 11, display: 'inline-block',
              transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform 0.15s ease', cursor: 'pointer', padding: 4,
            }}
          >▾</span>
        </div>
      </div>
      {open && <div>{children}</div>}
    </div>
  );
}
